import { db } from "./database";
import type { AssetFileRecord, AssetVariant } from "../domain/project";

const rowId = (projectId: string, assetId: string, variant: AssetVariant) =>
  `${projectId}:${assetId}:${variant}`;

async function getAssetDirectory(projectId: string, assetId: string, create: boolean) {
  const storage = navigator.storage as StorageManager & {
    getDirectory?: () => Promise<FileSystemDirectoryHandle>;
  };
  if (!storage.getDirectory) throw new Error("OPFS unavailable");
  const root = await storage.getDirectory();
  const projects = await root.getDirectoryHandle("projects", { create });
  const project = await projects.getDirectoryHandle(projectId, { create });
  const assets = await project.getDirectoryHandle("assets", { create });
  return assets.getDirectoryHandle(assetId, { create });
}

async function writeOpfs(projectId: string, assetId: string, variant: AssetVariant, blob: Blob) {
  const dir = await getAssetDirectory(projectId, assetId, true);
  const handle = await dir.getFileHandle(variant, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

async function readOpfs(projectId: string, assetId: string, variant: AssetVariant) {
  const dir = await getAssetDirectory(projectId, assetId, false);
  const handle = await dir.getFileHandle(variant);
  return handle.getFile();
}

export async function writeAssetFile(
  projectId: string,
  assetId: string,
  variant: AssetVariant,
  blob: Blob,
) {
  try {
    await writeOpfs(projectId, assetId, variant, blob);
    return "opfs" as const;
  } catch {
    const row: AssetFileRecord = {
      id: rowId(projectId, assetId, variant),
      projectId,
      assetId,
      variant,
      blob,
    };
    await db.assetFiles.put(row);
    return "indexeddb" as const;
  }
}

export async function readAssetFile(projectId: string, assetId: string, variant: AssetVariant) {
  try {
    const file = await readOpfs(projectId, assetId, variant);
    if (file.type) return file;
    const type = variant === "original"
      ? (await db.assets.get(assetId))?.mimeType || "application/octet-stream"
      : "image/webp";
    return new Blob([await file.arrayBuffer()], { type });
  } catch {
    const row = await db.assetFiles.get(rowId(projectId, assetId, variant));
    if (!row) throw new Error(`找不到图片文件: ${assetId}/${variant}`);
    return row.blob;
  }
}

export async function deleteAssetFiles(projectId: string, assetId: string) {
  try {
    const storage = navigator.storage as StorageManager & {
      getDirectory?: () => Promise<FileSystemDirectoryHandle>;
    };
    if (storage.getDirectory) {
      const root = await storage.getDirectory();
      const projects = await root.getDirectoryHandle("projects");
      const project = await projects.getDirectoryHandle(projectId);
      const assets = await project.getDirectoryHandle("assets");
      await assets.removeEntry(assetId, { recursive: true });
    }
  } catch {
    // A missing OPFS directory is expected when IndexedDB fallback was used.
  }
  await db.assetFiles.where("assetId").equals(assetId).delete();
}
